/**
 * TanStack Query hooks — the only way screens read/write data.
 * Query keys are arrays rooted by area so invalidation is precise.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./index";
import type * as T from "./types";

export const keys = {
  session: ["session"] as const,
  subscription: ["subscription"] as const,
  checkoutOffer: (country?: string | null) =>
    ["subscription", "checkout", country ?? null] as const,
  subscriptionAccount: ["subscription", "account"] as const,
  checkoutQuote: (choice: T.CheckoutChoice) =>
    ["subscription", "checkout", "quote", choice] as const,
  overview: ["overview"] as const,
  invoices: (q?: T.ListQuery) => ["invoices", q ?? {}] as const,
  invoice: (id: T.Id) => ["invoice", id] as const,
  creditNotes: (q?: T.ListQuery) => ["creditNotes", q ?? {}] as const,
  creditNote: (id: T.Id) => ["creditNote", id] as const,
  receipts: (q?: T.ListQuery) => ["receipts", q ?? {}] as const,
  receipt: (id: T.Id) => ["receipt", id] as const,
  bills: (q?: T.ListQuery) => ["bills", q ?? {}] as const,
  bill: (id: T.Id) => ["bill", id] as const,
  billKpis: ["bills", "kpis"] as const,
  fundTransfers: (q?: T.ListQuery) => ["fundTransfers", q ?? {}] as const,
  fundTransfer: (id: T.Id) => ["fundTransfer", id] as const,
  accounts: ["accounts"] as const,
  bankAccounts: ["bankAccounts"] as const,
  approvals: ["approvals"] as const,
  taxCodes: ["taxCodes"] as const,
  settings: (section: string) => ["settings", section] as const,
  help: ["help"] as const,
  helpHome: ["help", "home"] as const,
  helpCategory: (slug: string) => ["help", "category", slug] as const,
  helpArticle: (slug: string) => ["help", "article", slug] as const,
  helpSearch: (q: string) => ["help", "search", q] as const,
  supportCategories: ["support", "categories"] as const,
  supportTickets: (q?: { history: boolean; search?: string }) =>
    ["support", "tickets", q ?? {}] as const,
  supportTicket: (id: T.Id) => ["support", "ticket", id] as const,
};

export const useSession = () =>
  useQuery({ queryKey: keys.session, queryFn: api.session.get, staleTime: Infinity });
/** The calendar the pack uses (ADR-0033). Gregorian until the session says otherwise. */
export const useCalendar = (): T.Calendar => useSession().data?.calendar ?? "gregorian";
export const useOverview = () => useQuery({ queryKey: keys.overview, queryFn: api.overview.get });

/**
 * Where the organisation stands with RigoSub (ADR-0009) — the banner in the shell and, when it
 * says so, the screen that replaces the shell.
 *
 * A minute of staleness, matched to the API's own cache TTL, and no interval: the API already
 * holds this for a minute and is itself invalidated by RigoSub's status-changed event, so polling
 * harder buys nothing but load on a billing system. `refetchOnWindowFocus` is on here against the
 * client default because this is the one query whose answer changes while nobody is looking —
 * somebody pays, and the tab they come back to must stop refusing them.
 */
export const useSubscriptionStatus = () =>
  useQuery({
    queryKey: keys.subscription,
    queryFn: api.subscription.status,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    // A failed status lookup is not a reason to shout, and never a reason to block: the shell
    // renders no banner and carries on, which is the same thing the API does when RigoSub is
    // unreachable.
    retry: false,
  });

/** What can be bought, from RigoSub (suite ADR-0097). Fresh each time the checkout opens. */
export const useCheckoutOffer = (country?: string | null) =>
  useQuery({
    queryKey: keys.checkoutOffer(country),
    queryFn: () => api.subscription.checkoutOffer(country),
    staleTime: 0,
    retry: false,
    // Another country re-prices the page; the last prices stay on it until the new ones come.
    placeholderData: (previous) => previous,
  });

/** A paid subscription as an administrator manages it (suite ADR-0098). */
export const useSubscriptionAccount = (enabled: boolean) =>
  useQuery({
    queryKey: keys.subscriptionAccount,
    queryFn: api.subscription.account,
    enabled,
    retry: false,
  });

/** The subscription's changes: each answers with the account, which replaces the cached one. */
export function useSubscriptionChanges() {
  const queryClient = useQueryClient();
  const settle = (account: T.SubscriptionAccount) => {
    queryClient.setQueryData(keys.subscriptionAccount, account);
    void queryClient.invalidateQueries({ queryKey: keys.subscription, exact: true });
  };
  return {
    schedule: useMutation({ mutationFn: api.subscription.scheduleChange, onSuccess: settle }),
    undo: useMutation({ mutationFn: api.subscription.undoChange, onSuccess: settle }),
    billing: useMutation({ mutationFn: api.subscription.updateBilling, onSuccess: settle }),
  };
}

/** What a choice costs today. The last quote stays on screen while the next is asked for. */
export const useCheckoutQuote = (choice: T.CheckoutChoice | null) =>
  useQuery({
    queryKey: keys.checkoutQuote(
      choice ?? { planCode: "", cycle: "MONTHLY", addOns: [], extraUsers: 0 },
    ),
    queryFn: () => api.subscription.quoteCheckout(choice!),
    enabled: choice !== null,
    placeholderData: (prev) => prev,
    retry: false,
  });

export const useInvoices = (q: T.ListQuery) =>
  useQuery({
    queryKey: keys.invoices(q),
    queryFn: () => api.invoices.list(q),
    placeholderData: (prev) => prev,
  });
export const useInvoice = (id: T.Id) =>
  useQuery({ queryKey: keys.invoice(id), queryFn: () => api.invoices.get(id) });
export const useQuotations = (q: T.ListQuery) =>
  useQuery({
    queryKey: ["quotations", q],
    queryFn: () => api.quotations.list(q),
    placeholderData: (prev) => prev,
  });
export const useQuotation = (id: T.Id) =>
  useQuery({ queryKey: ["quotations", "one", id], queryFn: () => api.quotations.get(id) });
export const useCreditNotes = (q: T.ListQuery) =>
  useQuery({
    queryKey: keys.creditNotes(q),
    queryFn: () => api.creditNotes.list(q),
    placeholderData: (prev) => prev,
  });
export const useCreditNote = (id: T.Id) =>
  useQuery({ queryKey: keys.creditNote(id), queryFn: () => api.creditNotes.get(id) });
export const useReceipts = (q: T.ListQuery) =>
  useQuery({
    queryKey: keys.receipts(q),
    queryFn: () => api.receipts.list(q),
    placeholderData: (prev) => prev,
  });
export const useReceipt = (id: T.Id) =>
  useQuery({ queryKey: keys.receipt(id), queryFn: () => api.receipts.get(id) });
export const useBills = (q: T.ListQuery) =>
  useQuery({
    queryKey: keys.bills(q),
    queryFn: () => api.bills.list(q),
    placeholderData: (prev) => prev,
  });
export const useBill = (id: T.Id) =>
  useQuery({ queryKey: keys.bill(id), queryFn: () => api.bills.get(id) });
export const useBillKpis = () => useQuery({ queryKey: keys.billKpis, queryFn: api.bills.kpis });
export const useFundTransfers = (q: T.ListQuery) =>
  useQuery({
    queryKey: keys.fundTransfers(q),
    queryFn: () => api.fundTransfers.list(q),
    placeholderData: (prev) => prev,
  });
export const useFundTransfer = (id: T.Id) =>
  useQuery({ queryKey: keys.fundTransfer(id), queryFn: () => api.fundTransfers.get(id) });
export const useJournals = (q: T.ListQuery) =>
  useQuery({
    queryKey: ["journals", q],
    queryFn: () => api.journals.list(q),
    placeholderData: (prev) => prev,
  });
export const useJournal = (id: T.Id, enabled = true) =>
  useQuery({ queryKey: ["journals", "one", id], queryFn: () => api.journals.get(id), enabled });

/** Posting moves every balance, so everything that reads the ledger reloads. */
const journalSettled = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all([qc.invalidateQueries({ queryKey: ["journals"] }), settled(qc)]);

export function useSaveJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draft: T.JournalDraft) => api.journals.saveDraft(draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journals"] }),
  });
}

export function usePostJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.journals.post(id),
    onSuccess: () => journalSettled(qc),
  });
}

export function useDiscardJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.journals.discard(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journals"] }),
  });
}

export const useAccounts = () =>
  useQuery({ queryKey: keys.accounts, queryFn: api.accounts.list, staleTime: 60_000 });
export const useChartRoles = () =>
  useQuery({ queryKey: [...keys.accounts, "roles"], queryFn: api.accounts.roles });
export const useBankAccounts = () =>
  useQuery({ queryKey: keys.bankAccounts, queryFn: api.accounts.bankAccounts, staleTime: 60_000 });
export const useBankLedgerAccounts = () =>
  useQuery({
    queryKey: [...keys.bankAccounts, "ledger"],
    queryFn: api.accounts.bankLedgerAccounts,
  });

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: T.NewBankAccount) => api.accounts.createBankAccount(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.bankAccounts }),
  });
}

export const useParties = (kind: T.Party["kind"], q: string) =>
  useQuery({
    queryKey: ["parties", kind, q],
    queryFn: () => api.parties.list(kind, q),
    placeholderData: (prev) => prev,
  });
export const useReportFrameworks = (enabled = true) =>
  useQuery({
    queryKey: ["reporting", "frameworks"] as const,
    queryFn: () => api.reporting.frameworks(),
    staleTime: 300_000,
    enabled,
  });
export const useReportTemplates = (enabled = true) =>
  useQuery({
    queryKey: ["reporting", "templates"] as const,
    queryFn: () => api.reporting.templates(),
    staleTime: 300_000,
    enabled,
  });
export const useReportTemplate = (code: string) =>
  useQuery({
    queryKey: ["reporting", "templates", code] as const,
    queryFn: () => api.reporting.template(code),
  });
export const useTemplatePreview = (preview: T.TemplatePreview, enabled = true) =>
  useQuery({
    queryKey: ["reporting", "preview", preview] as const,
    queryFn: () => api.reporting.previewTemplate(preview),
    enabled,
    // The last good preview stays up while the next one loads.
    placeholderData: (previous) => previous,
    retry: false,
  });

export function useCloneTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.reporting.cloneTemplate(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporting", "templates"] }),
  });
}

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, lines }: { id: T.Id; name: string; lines: T.TemplateLine[] }) =>
      api.reporting.saveTemplate(id, name, lines),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["reporting", "templates"] }),
        qc.invalidateQueries({ queryKey: ["reporting", "statement"] }),
      ]),
  });
}

/** A company's own report tags (ADR-0065): each change reloads the frameworks, and the mapping and statements that read them. */
function useReportTagMutation<V>(run: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["reporting", "frameworks"] }),
        qc.invalidateQueries({ queryKey: ["reporting", "mapping"] }),
        qc.invalidateQueries({ queryKey: ["reporting", "statement"] }),
      ]),
  });
}

export const useAddReportTag = () =>
  useReportTagMutation(({ framework, tag }: { framework: string; tag: T.NewCompanyTag }) =>
    api.reporting.addTag(framework, tag),
  );

export const useRenameReportTag = () =>
  useReportTagMutation(({ framework, id, label }: { framework: string; id: T.Id; label: string }) =>
    api.reporting.renameTag(framework, id, label),
  );

export const useArchiveReportTag = () =>
  useReportTagMutation(({ framework, id }: { framework: string; id: T.Id }) =>
    api.reporting.archiveTag(framework, id),
  );

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.reporting.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporting", "templates"] }),
  });
}

export const useStatement = (query: T.StatementQuery, enabled = true) =>
  useQuery({
    queryKey: ["reporting", "statement", query] as const,
    queryFn: () => api.reporting.statement(query),
    enabled,
  });
export const useStatementSnapshot = (id: T.Id, enabled = true) =>
  useQuery({
    queryKey: ["reporting", "snapshot", id] as const,
    queryFn: () => api.reporting.snapshot(id),
    enabled,
  });

export function useFinalizeStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (query: T.StatementQuery) => api.reporting.finalize(query),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporting", "statement"] }),
  });
}
export const useAccountMapping = (framework: string) =>
  useQuery({
    queryKey: ["reporting", "mapping", framework] as const,
    queryFn: () => api.reporting.mapping(framework),
  });
export const useAccountMappingHistory = (framework: string, accountId: T.Id) =>
  useQuery({
    queryKey: ["reporting", "mapping", framework, accountId] as const,
    queryFn: () => api.reporting.mappingHistory(framework, accountId),
  });

export function useSaveAccountMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ framework, decisions }: { framework: string; decisions: T.MappingDecision[] }) =>
      api.reporting.saveMapping(framework, decisions),
    // A statement reads through the mapping, so it's out of date too.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["reporting", "mapping"] }),
        qc.invalidateQueries({ queryKey: ["reporting", "statement"] }),
      ]),
  });
}

export const useEmployees = () =>
  useQuery({ queryKey: ["employees"] as const, queryFn: () => api.employees.list() });

export function useRecordEmployeeMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (movement: T.NewEmployeeMovement) => api.employees.record(movement),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["employees"] }), settled(qc)]),
  });
}

export const useEmployeeMovementDrafts = () =>
  useQuery({ queryKey: ["employees", "drafts"] as const, queryFn: () => api.employees.drafts() });
export const useEmployeeMovement = (id: T.Id) =>
  useQuery({
    queryKey: ["employees", "movement", id] as const,
    queryFn: () => api.employees.get(id),
  });

export function usePostEmployeeMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.employees.post(id),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["employees"] }), settled(qc)]),
  });
}

export function useDiscardEmployeeMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.employees.discard(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["employees"] }),
  });
}

export const usePartyDetails = (id: T.Id, enabled = true) =>
  useQuery({
    queryKey: ["parties", "details", id],
    queryFn: () => api.parties.details(id),
    enabled,
  });

export const useReceivableAnalysis = (asAt: T.IsoDate) =>
  useQuery({
    queryKey: ["reports", "receivableAnalysis", asAt],
    queryFn: () => api.reports.receivableAnalysis(asAt),
  });

export const usePayableAnalysis = (asAt: T.IsoDate) =>
  useQuery({
    queryKey: ["reports", "payableAnalysis", asAt],
    queryFn: () => api.reports.payableAnalysis(asAt),
  });

export const useOpenItems = (
  kind: "customer" | "supplier",
  dueFrom: T.IsoDate | undefined,
  dueTo: T.IsoDate | undefined,
) =>
  useQuery({
    queryKey: ["reports", "openItems", kind, dueFrom, dueTo],
    queryFn: () => api.reports.openItems(kind, dueFrom, dueTo),
  });

export const usePartyStanding = (
  id: T.Id | undefined,
  kind: "customer" | "supplier",
  asAt: T.IsoDate,
  enabled = true,
) =>
  useQuery({
    queryKey: ["parties", "standing", id, kind, asAt],
    queryFn: () => api.parties.standing(id!, kind, asAt),
    enabled: enabled && id !== undefined,
  });

export const useSupplierReconciliations = (id: T.Id, enabled = true) =>
  useQuery({
    queryKey: ["parties", "reconciliations", id],
    queryFn: () => api.parties.reconciliations(id),
    enabled,
  });

export function useReconcileStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      statementDate,
      statementBalance,
    }: {
      id: T.Id;
      statementDate: T.IsoDate;
      statementBalance: T.Money;
    }) => api.parties.reconcile(id, statementDate, statementBalance),
    onSuccess: (_, { id }) =>
      qc.invalidateQueries({ queryKey: ["parties", "reconciliations", id] }),
  });
}

export const usePartyStatement = (
  id: T.Id,
  kind: T.Party["kind"],
  from: T.IsoDate,
  to: T.IsoDate,
  enabled = true,
) =>
  useQuery({
    queryKey: ["parties", "statement", id, kind, from, to],
    queryFn: () => api.parties.statement(id, kind, from, to),
    enabled,
  });

export const useOpenInvoices = (customerId: T.Id) =>
  useQuery({
    queryKey: ["invoices", "open", customerId],
    queryFn: () => api.receipts.openInvoicesFor(customerId),
  });

export function useSaveParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, form }: { id: T.Id | undefined; form: T.PartyForm }) =>
      id === undefined ? api.parties.create(form) : api.parties.update(id, form),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["parties"] }),
        qc.invalidateQueries({ queryKey: ["employees"] }),
      ]),
  });
}

export const useBankReconciliation = (accountId: T.Id | undefined, asAt: T.IsoDate) =>
  useQuery({
    queryKey: ["banking", "reconciliation", accountId, asAt],
    queryFn: () => api.banking.reconciliation(accountId!, asAt),
    enabled: accountId !== undefined && asAt !== "",
  });

/** One read per account. A company has a handful of them. */
export const useBankReconciliations = (accountIds: T.Id[], asAt: T.IsoDate, enabled = true) =>
  useQueries({
    queries: accountIds.map((id) => ({
      queryKey: ["banking", "reconciliation", id, asAt],
      queryFn: () => api.banking.reconciliation(id, asAt),
      enabled,
    })),
  });

export const useStatementMatches = (accountId: T.Id | undefined, asAt: T.IsoDate) =>
  useQuery({
    queryKey: ["banking", "matches", accountId, asAt],
    queryFn: () => api.banking.matches(accountId!, asAt),
    enabled: accountId !== undefined && asAt !== "",
  });

export const useMatchCandidates = (lineId: T.Id) =>
  useQuery({
    queryKey: ["banking", "candidates", lineId],
    queryFn: () => api.banking.candidates(lineId),
  });

export function useMatchStatementLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { lineId: T.Id; journalId: T.Id; journalLineId?: T.Id }) =>
      api.banking.match(input.lineId, input.journalId, input.journalLineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export function useMatchStatementLineToSeveral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      lineId: T.Id;
      movements: Array<{ journalId: T.Id; journalLineId: T.Id }>;
    }) => api.banking.matchSeveral(input.lineId, input.movements),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export const useReconciliationLocks = (accountId: T.Id | undefined) =>
  useQuery({
    queryKey: ["banking", "locks", accountId],
    queryFn: () => api.banking.locks(accountId!),
    enabled: accountId !== undefined,
  });

export function useLockReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { accountId: T.Id; asAt: T.IsoDate; statementBalance: T.Money | null }) =>
      api.banking.lock(input.accountId, input.asAt, input.statementBalance),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export function useReopenReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lockId: T.Id) => api.banking.reopen(lockId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export function useCreateStatementEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineId,
      ...input
    }: {
      lineId: T.Id;
      accountId: T.Id;
      memo: string;
      movements?: Array<{ journalId: T.Id; journalLineId: T.Id }>;
    }) => api.banking.createEntry(lineId, input),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["banking"] }), settled(qc)]),
  });
}

export function useUnmatchStatementLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: T.Id) => api.banking.unmatch(matchId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export function useImportStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.banking.importStatement>[0]) =>
      api.banking.importStatement(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banking"] }),
  });
}

export const useBillingItems = (q: { q?: string; accountId?: T.Id; category?: T.VatCategory }) =>
  useQuery({
    queryKey: ["billingItems", q],
    queryFn: () => api.billingItems.list(q),
  });

export function useSaveBillingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, form }: { id: T.Id | undefined; form: T.BillingItemForm }) =>
      id === undefined ? api.billingItems.create(form) : api.billingItems.update(id, form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billingItems"] }),
  });
}

export function useDeleteBillingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.billingItems.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billingItems"] }),
  });
}

export const useApprovals = (enabled = true) =>
  useQuery({ queryKey: keys.approvals, queryFn: api.approvals.queue, enabled });
/** The session carries the names, so saving them reloads it and every screen follows. */
export function useSaveTaxNames() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (names: Omit<T.TaxNames, "packName">) => api.settings.saveTaxNames(names),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.session }),
  });
}

export function useRenameAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: T.Id; name: string }) => api.accounts.rename(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accounts }),
  });
}

/** With no account id, it adds the supplied chart's account for that purpose. */
export function useMapChartRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ role, accountId }: { role: string; accountId: T.Id | null }) =>
      accountId === null
        ? api.accounts.addRoleAccount(role)
        : api.accounts.mapRole(role, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accounts }),
  });
}

export function useRenameTaxCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: T.Id; name: string }) => api.settings.renameTaxCode(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.taxCodes }),
  });
}

export function useRenameWithholdingCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, name }: { code: string; name: string }) =>
      api.settings.renameWithholdingCode(code, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["withholdingCodes"] }),
  });
}

export const useTaxCodes = () =>
  useQuery({ queryKey: keys.taxCodes, queryFn: api.settings.taxCodes, staleTime: 60_000 });

export const useProfitAndLoss = (params: { period: string; compare?: string }) =>
  useQuery({
    queryKey: ["profitAndLoss", params] as const,
    queryFn: () => api.reports.profitAndLoss(params),
  });

export function useSignOut() {
  return useMutation({
    mutationFn: () => api.session.signOut(),
    // A full load, not a cache update: nothing fetched for this person may outlive their session.
    onSettled: () => window.location.assign("/"),
  });
}

export function useUpdateProfile() {
  return useMutation({
    mutationFn: (input: { fullName: string }) => api.session.updateProfile(input),
    // The name is on the session, and every screen that shows it reads that.
    onSuccess: () => window.location.reload(),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.session.changePassword(input),
  });
}

export function useSwitchOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: T.Id) => api.session.switchOrganization(orgId),
    onSuccess: (session) => {
      qc.setQueryData(keys.session, session);
      // Everything tenant-scoped must reload.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "session" });
    },
  });
}

/** Opens the demo company, building it the first time (ADR-0063). The session moves there, so everything reloads. */
export function useOpenDemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.session.openDemo(),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function usePreviewAsRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (role: T.RoleName) =>
      api.session.previewAsRole ? api.session.previewAsRole(role) : api.session.get(),
    onSuccess: (session) => qc.setQueryData(keys.session, session),
  });
}

/**
 * Writes.
 *
 * Every one of these existed only as a direct `api.*` call inside a screen, or not at all — the
 * approval dialog's confirm button was `onClick={() => setActing(null)}` and "Save draft" on a bill
 * was `onClick={() => navigate(...)}`. Going through a mutation is what makes the lists, the KPIs
 * and the approval queue reload afterwards: a write that does not invalidate leaves the person
 * looking at the figures from before it, which reads as the write having failed.
 *
 * `settled` is the invalidation a money movement needs. A receipt changes the invoice it settles,
 * the receipts list, what the customer owes and the headline receivable — so all of them reload,
 * rather than each screen remembering which of its neighbours it has just invalidated.
 */
function settled(qc: ReturnType<typeof useQueryClient>) {
  const areas = new Set([
    "invoices",
    "invoice",
    "receipts",
    "receipt",
    "creditNotes",
    "creditNote",
    "bills",
    "bill",
    "fundTransfers",
    "fundTransfer",
    "journals",
    "journal",
    "payments",
    "purchaseOrders",
    "approvals",
    "overview",
    "parties",
  ]);

  return qc.invalidateQueries({ predicate: (q) => areas.has(String(q.queryKey[0])) });
}

export const useYearChecklist = (fiscalYearId: T.Id | undefined) =>
  useQuery({
    queryKey: ["yearChecklist", fiscalYearId] as const,
    queryFn: () => api.settings.yearChecklist(fiscalYearId!),
    enabled: fiscalYearId !== undefined,
  });

/** A refusal is logged, so the checklist reloads either way. */
const useYearCheckAct = <V>(fiscalYearId: T.Id, act: (variables: V) => Promise<void>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: act,
    onSettled: () => qc.invalidateQueries({ queryKey: ["yearChecklist", fiscalYearId] }),
  });
};

export const useReviewCheckItem = (fiscalYearId: T.Id) =>
  useYearCheckAct(
    fiscalYearId,
    ({ workstream, key, reviewed }: { workstream: string; key: string; reviewed: boolean }) =>
      api.settings.reviewCheckItem(fiscalYearId, workstream, key, reviewed),
  );
export const useMarkCheckDone = (fiscalYearId: T.Id) =>
  useYearCheckAct(
    fiscalYearId,
    ({ workstream, ...done }: { workstream: string; evidence: string; note?: string }) =>
      api.settings.markCheckDone(fiscalYearId, workstream, done),
  );
export const useMarkCheckNotApplicable = (fiscalYearId: T.Id) =>
  useYearCheckAct(fiscalYearId, ({ workstream, note }: { workstream: string; note: string }) =>
    api.settings.markCheckNotApplicable(fiscalYearId, workstream, note),
  );
export const useReopenCheck = (fiscalYearId: T.Id) =>
  useYearCheckAct(fiscalYearId, (workstream: string) =>
    api.settings.reopenCheck(fiscalYearId, workstream),
  );

export const useYearEndMethod = () =>
  useQuery({ queryKey: ["yearEndMethod"] as const, queryFn: () => api.settings.yearEndMethod() });

export const useYearEndPreview = (fiscalYearId: T.Id | undefined) =>
  useQuery({
    queryKey: ["yearEndPreview", fiscalYearId] as const,
    queryFn: () => api.settings.yearEndPreview(fiscalYearId!),
    enabled: fiscalYearId !== undefined,
  });

export function useSaveYearEndMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (method: T.YearEndMethod) => api.settings.saveYearEndMethod(method),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["yearEndMethod"] }),
        qc.invalidateQueries({ queryKey: ["yearEndPreview"] }),
      ]),
  });
}

export function useOpenNextYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscalYearId: T.Id) => api.settings.openNextYear(fiscalYearId),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["yearEndPreview"] }),
        qc.invalidateQueries({ queryKey: keys.settings("fiscalYears") }),
      ]),
  });
}

export function useHardClosePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, reason }: { periodId: T.Id; reason: string }) =>
      api.settings.hardClosePeriod(periodId, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yearEndPreview"] }),
  });
}

/** Closing a year moves the ledger and every year's status. */
export function useCloseYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fiscalYearId,
      ...close
    }: {
      fiscalYearId: T.Id;
      reason: string;
      journalId?: T.Id;
      manual?: boolean;
      revision?: number;
    }) => api.settings.closeYear(fiscalYearId, close),
    // A refused close is in the log too, so that reads again either way.
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["yearChecklist"] }),
        // A close refused because the books moved has to show the figures as they are now.
        qc.invalidateQueries({ queryKey: ["yearEndPreview"] }),
        qc.invalidateQueries({ queryKey: ["yearWorkpaper"] }),
        qc.invalidateQueries({ queryKey: ["yearCloseGates"] }),
      ]),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: keys.settings("fiscalYears") }), settled(qc)]),
  });
}

export const useYearCloseGates = (fiscalYearId: T.Id | undefined) =>
  useQuery({
    queryKey: ["yearCloseGates", fiscalYearId] as const,
    queryFn: () => api.settings.yearCloseGates(fiscalYearId!),
    enabled: fiscalYearId !== undefined,
  });

/** What the soft lock and a withdrawal change. Every part of the page reads one of these. */
const yearCloseMoved = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all(
    ["yearCloseGates", "yearEndPreview", "yearChecklist", "documentApproval"].map((key) =>
      qc.invalidateQueries({ queryKey: [key] }),
    ),
  );

export function useSetYearSoftLock(fiscalYearId: T.Id | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => api.settings.setYearSoftLock(fiscalYearId!, on),
    onSettled: () => yearCloseMoved(qc),
  });
}

export function useWithdrawYearClose(fiscalYearId: T.Id | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => api.settings.withdrawYearClose(fiscalYearId!, reason),
    onSettled: () => yearCloseMoved(qc),
  });
}

export const useYearWorkpaper = (fiscalYearId: T.Id | undefined) =>
  useQuery({
    queryKey: ["yearWorkpaper", fiscalYearId] as const,
    queryFn: () => api.settings.yearWorkpaper(fiscalYearId!),
    enabled: fiscalYearId !== undefined,
    // Other people keep posting. This is how a preview left open finds out it's out of date.
    refetchInterval: 30_000,
  });

/** The workpaper as a workbook. It goes in the close log, so that reads again. */
export function useExportYearWorkpaper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fiscalYearId: T.Id) => api.settings.exportYearWorkpaper(fiscalYearId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["yearChecklist"] }),
  });
}

/** An administrator reopens a closed year (ADR-0058). */
export function useReopenYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fiscalYearId,
      ...reopen
    }: {
      fiscalYearId: T.Id;
      periodIds: T.Id[];
      accountIds: T.Id[];
      reason: string;
    }) => api.settings.reopenYear(fiscalYearId, reopen),
    onSettled: () => qc.invalidateQueries({ queryKey: ["yearChecklist"] }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["yearEndPreview"] }),
        qc.invalidateQueries({ queryKey: keys.settings("fiscalYears") }),
      ]),
  });
}

export function useSaveBillDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewBill) => api.bills.saveDraft(dto),
    onSuccess: () => settled(qc),
  });
}

export function useApproveBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.bills.approve(id),
    onSuccess: () => settled(qc),
  });
}

export function useSubmitBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewBill) => api.bills.submit(dto),
    onSuccess: () => settled(qc),
  });
}

export const useDebitNotes = (q: T.ListQuery) =>
  useQuery({
    queryKey: ["debitNotes", q],
    queryFn: () => api.debitNotes.list(q),
    placeholderData: (prev) => prev,
  });
export const useDebitNote = (id: T.Id) =>
  useQuery({ queryKey: ["debitNotes", "one", id], queryFn: () => api.debitNotes.get(id) });

export function useSaveDebitNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewDebitNote) => api.debitNotes.saveDraft(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["debitNotes"] }),
  });
}

/** Approving posts and lowers a bill, so what reads either reloads. */
export function useApproveDebitNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.debitNotes.approve(id),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["debitNotes"] }),
        qc.invalidateQueries({ queryKey: ["bills"] }),
        qc.invalidateQueries({ queryKey: ["payments"] }),
        settled(qc),
      ]),
  });
}

export const useGeneralLedger = (
  params: Parameters<typeof api.reports.generalLedger>[0],
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["reports", "generalLedger", params],
    queryFn: () => api.reports.generalLedger(params),
    enabled,
    placeholderData: (prev) => prev,
  });
export const useLedgerDocuments = (
  params: Parameters<typeof api.reports.ledgerDocuments>[0],
  enabled: boolean,
) =>
  useQuery({
    queryKey: ["reports", "ledgerDocuments", params],
    queryFn: () => api.reports.ledgerDocuments(params),
    enabled,
  });
export const useTrialBalanceAsOn = (asOn: T.IsoDate, enabled = true) =>
  useQuery({
    queryKey: ["reports", "trialBalanceAsOn", asOn],
    queryFn: () => api.reports.trialBalanceAsOn(asOn),
    enabled: enabled && asOn !== "",
    placeholderData: (prev) => prev,
  });

export const useCashFlow = (from: T.IsoDate, to: T.IsoDate, enabled: boolean) =>
  useQuery({
    queryKey: ["reports", "cashFlow", from, to],
    queryFn: () => api.reports.cashFlow(from, to),
    enabled,
    placeholderData: (prev) => prev,
  });
export const useAgeingReport = (kind: "receivable" | "payable", asAt: T.IsoDate) =>
  useQuery({
    queryKey: ["reports", "ageing", kind, asAt],
    queryFn: () => api.reports.ageing(kind, asAt),
    placeholderData: (prev) => prev,
  });
export const useVatReturn = (from: T.IsoDate, to: T.IsoDate) =>
  useQuery({
    queryKey: ["reports", "vatReturn", from, to],
    queryFn: () => api.reports.vatReturn(from, to),
    placeholderData: (prev) => prev,
  });
export const useWithholdingReport = (from: T.IsoDate, to: T.IsoDate) =>
  useQuery({
    queryKey: ["reports", "withholding", from, to],
    queryFn: () => api.reports.withholding(from, to),
    placeholderData: (prev) => prev,
  });

export const useInvoiceCredits = (id: T.Id, enabled = true) =>
  useQuery({
    queryKey: ["invoices", "credits", id],
    queryFn: () => api.invoices.credits(id),
    enabled,
  });

/** Using a credit lowers an invoice and, for an advance, moves the ledger. */
export function useApplyInvoiceCredit(id: T.Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (credit: { kind: T.CustomerCreditKind; sourceId: T.Id; amount: T.Money }) =>
      api.invoices.useCredit(id, credit),
    onSuccess: () => settled(qc),
  });
}

export const useBillCredits = (id: T.Id, enabled = true) =>
  useQuery({ queryKey: ["bills", "credits", id], queryFn: () => api.bills.credits(id), enabled });

/** Using a credit lowers a bill and, for an advance, moves the ledger. */
export function useApplyBillCredit(id: T.Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (credit: { kind: T.SupplierCreditKind; sourceId: T.Id; amount: T.Money }) =>
      api.bills.useCredit(id, credit),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["bills"] }),
        qc.invalidateQueries({ queryKey: ["payments"] }),
        qc.invalidateQueries({ queryKey: ["debitNotes"] }),
        settled(qc),
      ]),
  });
}

export const useSupplierPayments = (q: T.ListQuery) =>
  useQuery({
    queryKey: ["payments", q],
    queryFn: () => api.payments.list(q),
    placeholderData: (prev) => prev,
  });
export const useSupplierPayment = (id: T.Id) =>
  useQuery({ queryKey: ["payments", "one", id], queryFn: () => api.payments.get(id) });
/** The bills a debit note can correct, which includes one already paid (ADR-0040). */
export const useCorrectableBills = (supplierId: T.Id | undefined, enabled = true) =>
  useQuery({
    queryKey: ["bills", "correctable", supplierId ?? "all"],
    queryFn: () => api.bills.correctable(supplierId),
    enabled,
  });

export const useOpenBills = (supplierId: T.Id | undefined, enabled = true) =>
  useQuery({
    queryKey: ["payments", "openBills", supplierId ?? "all"],
    queryFn: () => api.payments.openBills(supplierId),
    enabled,
  });
export const useDocumentBranding = () =>
  useQuery({
    queryKey: keys.settings("branding"),
    queryFn: api.settings.branding,
    staleTime: 60_000,
  });

function useBrandingWrite<A>(write: (argument: A) => Promise<T.DocumentBranding>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (saved) => qc.setQueryData(keys.settings("branding"), saved),
  });
}

export const useSaveDocumentBranding = () =>
  useBrandingWrite((branding: T.DocumentBranding) => api.settings.saveBranding(branding));

export const useSaveBrandingLogo = () =>
  useBrandingWrite((base64: string) => api.settings.saveBrandingLogo(base64));

export const useRemoveBrandingLogo = () =>
  useBrandingWrite((_: void) => api.settings.removeBrandingLogo());

export const useEmailDraft = (target: T.EmailTarget) =>
  useQuery({
    queryKey: ["emailDraft", target.kind, target.id] as const,
    queryFn: () => api.documents.emailDraft(target),
    // It's filled from the document and the template as they are now.
    staleTime: 0,
    gcTime: 0,
  });

export const useDocumentEmails = (of: T.EmailsOf) =>
  useQuery({
    queryKey: ["documentEmails", of.kind, of.id] as const,
    queryFn: () => api.documents.emails(of),
    // Sending takes a few seconds and happens elsewhere, so keep asking until it has settled.
    refetchInterval: (query) =>
      query.state.data?.some((email) =>
        email.recipients.some((r) => r.state === "preparing" || r.state === "sending"),
      )
        ? 5000
        : false,
  });

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target, email }: { target: T.EmailTarget; email: T.EmailToSend }) =>
      api.documents.sendEmail(target, email),
    // A reminder shows on the invoice and on the customer, so every list is asked again. Emailing a
    // quotation also marks it sent.
    onSuccess: (_sent, { target }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["documentEmails"] }),
        ...(target.kind === "quotation"
          ? [qc.invalidateQueries({ queryKey: ["quotations"] })]
          : []),
        ...(target.kind === "purchaseOrder"
          ? [qc.invalidateQueries({ queryKey: ["purchaseOrders"] })]
          : []),
      ]),
  });
}

export const useEmailTemplates = () =>
  useQuery({
    queryKey: keys.settings("emailTemplates"),
    queryFn: api.settings.emailTemplates,
    staleTime: 60_000,
  });

export function useSaveEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (template: Pick<T.EmailTemplate, "kind" | "subject" | "body">) =>
      api.settings.saveEmailTemplate(template),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("emailTemplates") }),
  });
}

export function useResetEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: string) => api.settings.resetEmailTemplate(kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("emailTemplates") }),
  });
}

export const useCountryPack = () =>
  useQuery({
    queryKey: keys.settings("countryPack"),
    queryFn: api.settings.countryPack,
    staleTime: 300_000,
    retry: false,
  });

export const useWithholdingCodes = () =>
  useQuery({
    queryKey: ["withholdingCodes"],
    queryFn: api.settings.withholdingCodes,
    staleTime: 60_000,
  });

/** A payment moves the bills it settles and the ledger, so everything that reads either reloads. */
export function useRecordSupplierPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewSupplierPayment) => api.payments.create(dto),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["payments"] }),
        qc.invalidateQueries({ queryKey: ["bills"] }),
        settled(qc),
      ]),
  });
}

export function usePostSupplierPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.payments.post(id),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["payments"] }),
        qc.invalidateQueries({ queryKey: ["bills"] }),
        settled(qc),
      ]),
  });
}

export function useDiscardSupplierPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.payments.discard(id),
    // Not awaited: the page that asked leaves at once, and waiting would refetch the deleted draft.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useRecordReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewReceipt) => api.receipts.create(dto),
    onSuccess: () => settled(qc),
  });
}

/** Deleting a draft moves no figure in the ledger, but drafts are counted on the lists and at home. */
export function useSaveQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewQuotation) => api.quotations.saveDraft(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });
}

export function useNumberQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.quotations.number(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });
}

export function useSendQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.quotations.send(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });
}

export function useRespondToQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accepted }: { id: T.Id; accepted: boolean }) =>
      api.quotations.respond(id, accepted),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });
}

export function useDuplicateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.quotations.duplicate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotations"] }),
  });
}

/** Makes an invoice, so everything that lists invoices reloads too. */
export function useConvertQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.quotations.convert(id),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["quotations"] }), settled(qc)]),
  });
}

export const usePurchaseOrders = (q: T.ListQuery) =>
  useQuery({
    queryKey: ["purchaseOrders", q],
    queryFn: () => api.purchaseOrders.list(q),
    placeholderData: (prev) => prev,
  });
export const usePurchaseOrder = (id: T.Id) =>
  useQuery({ queryKey: ["purchaseOrders", "one", id], queryFn: () => api.purchaseOrders.get(id) });

const usePurchaseOrderAct = <V>(act: (variables: V) => Promise<unknown>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: act,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchaseOrders"] }),
  });
};

export function useSavePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewPurchaseOrder) => api.purchaseOrders.saveDraft(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchaseOrders"] }),
  });
}

export const useNumberPurchaseOrder = () =>
  usePurchaseOrderAct((id: T.Id) => api.purchaseOrders.number(id));
export const useSendPurchaseOrder = () =>
  usePurchaseOrderAct((id: T.Id) => api.purchaseOrders.send(id));
export const useConfirmPurchaseOrder = () =>
  usePurchaseOrderAct((id: T.Id) => api.purchaseOrders.confirm(id));
export const useCancelPurchaseOrder = () =>
  usePurchaseOrderAct((id: T.Id) => api.purchaseOrders.cancel(id));
export const useReceivePurchaseOrder = () =>
  usePurchaseOrderAct(
    ({
      id,
      receivedOn,
      lines,
    }: {
      id: T.Id;
      receivedOn: T.IsoDate;
      lines: Array<{ lineId: T.Id; quantity: string }>;
    }) => api.purchaseOrders.receive(id, receivedOn, lines),
  );

/** Makes a bill, so everything that lists bills reloads too. */
export function useConvertPurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.purchaseOrders.convert(id),
    onSuccess: () =>
      Promise.all([qc.invalidateQueries({ queryKey: ["purchaseOrders"] }), settled(qc)]),
  });
}

export function useDiscardDraft(
  kind: "invoices" | "creditNotes" | "bills" | "debitNotes" | "quotations" | "purchaseOrders",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api[kind].discardDraft(id),
    // Not awaited: the page that asked leaves at once, and waiting would refetch the deleted draft
    // under it and show that it could not be loaded.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [kind] });
      void settled(qc);
    },
  });
}

export function useSaveInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewInvoice) => api.invoices.saveDraft(dto),
    onSuccess: () => settled(qc),
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.invoices.issue(id),
    onSuccess: () => settled(qc),
  });
}

export function useSaveCreditNoteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewCreditNote) => api.creditNotes.saveDraft(dto),
    onSuccess: () => settled(qc),
  });
}

export function useIssueCreditNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.creditNotes.issue(id),
    onSuccess: () => settled(qc),
  });
}

export function usePostFundTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.fundTransfers.post(id),
    onSuccess: () => settled(qc),
  });
}

export function useCreateFundTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: T.NewFundTransfer) => api.fundTransfers.create(dto),
    onSuccess: () => settled(qc),
  });
}

/** Verify, approve or return a document in the queue. Maker-checker, from the screen's side. */
export const useFiscalYears = () =>
  useQuery({ queryKey: keys.settings("fiscalYears"), queryFn: api.settings.fiscalYears });

export const useYearEnd = (fiscalYearId: T.Id | undefined) =>
  useQuery({
    queryKey: ["yearEnd", fiscalYearId] as const,
    queryFn: () => api.settings.yearEnd(fiscalYearId!),
    enabled: fiscalYearId !== undefined,
  });

/**
 * Moves the year-end close on a step.
 *
 * The answer replaces the query's data rather than invalidating it: the wizard is a conversation
 * about one fiscal year, and refetching between steps would show the person the beginning again.
 */
export function useAdvanceYearEnd(fiscalYearId: T.Id | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (step: T.YearEndStep) => api.settings.yearEndAdvance(fiscalYearId!, step),
    onSuccess: (state) => {
      qc.setQueryData(["yearEnd", fiscalYearId] as const, state);
      // The year closing changes what every other screen may do, so they reload.
      if (state.step === "done") void settled(qc);
    },
  });
}

/** Deals with one document blocking the close — the design's two buttons. */
export function useResolveYearEndDocument(fiscalYearId: T.Id | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: T.Id; how: "post" | "moveOut" }) =>
      api.settings.yearEndResolve(fiscalYearId!, input.documentId, input.how),
    onSuccess: (state) => {
      qc.setQueryData(["yearEnd", fiscalYearId] as const, state);
      // Posting or re-dating a document changes what the lists and the KPIs say.
      void settled(qc);
    },
  });
}

export const useUsers = (enabled = true) =>
  useQuery({ queryKey: keys.settings("users"), queryFn: api.settings.users, enabled });

export const useRoles = () =>
  useQuery({ queryKey: keys.settings("roles"), queryFn: api.settings.roles, staleTime: 60_000 });

export const useAuditTrail = (query: {
  q?: string;
  user?: string;
  kind?: T.AuditEntry["kind"] | "all";
}) =>
  useQuery({
    queryKey: ["settings", "audit", query] as const,
    queryFn: () => api.settings.audit(query),
    placeholderData: (previous) => previous,
  });

export const useNumbering = () =>
  useQuery({ queryKey: keys.settings("numbering"), queryFn: api.settings.numbering });

export function useSaveNumbering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: T.SaveNumbering) => api.settings.saveNumbering(input),
    onSuccess: (saved) => qc.setQueryData(keys.settings("numbering"), saved),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      fullName: string;
      email: string;
      role: T.RoleName;
      accessExpires: T.IsoDate | null;
    }) => api.settings.inviteUser(input),
    // The list and the roles matrix both change: a role's member count is who holds it.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });
}

export const useResendInvitation = () =>
  useMutation({ mutationFn: (id: T.Id) => api.settings.resendInvitation(id) });

export const useSetupSteps = (enabled = true) =>
  useQuery({
    queryKey: keys.settings("setupSteps"),
    queryFn: api.settings.setupSteps,
    enabled,
  });

export function useSaveSetupStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ step, status }: { step: string; status: T.SetupStepState["status"] }) =>
      api.settings.saveSetupStep(step, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("setupSteps") }),
  });
}

export const useSetupStatus = () =>
  useQuery({ queryKey: keys.settings("setup"), queryFn: api.settings.setup });

/** Everything the setup creates is read by some other screen, so all of it reloads. */
export function useRunSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.settings.runSetup(),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export const useApprovalPolicy = () =>
  useQuery({ queryKey: keys.settings("approvalPolicy"), queryFn: api.settings.approvalPolicy });

export function useSetApprovalPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: T.ApprovalPolicy & { reason: string }) =>
      api.settings.setApprovalPolicy(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("approvalPolicy") }),
  });
}

export function useSaveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profile: T.CompanyProfile) => api.settings.saveCompany(profile),
    onSuccess: (saved) => {
      qc.setQueryData(keys.settings("company"), saved);
      // The session's organisation name comes from this profile.
      void qc.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export const useInvoiceSetting = () =>
  useQuery({
    queryKey: keys.settings("invoicing"),
    queryFn: api.settings.invoicing,
    staleTime: 60_000,
  });

export function useSaveInvoiceSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (setting: T.InvoiceSetting) => api.settings.saveInvoicing(setting),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("invoicing") }),
  });
}

export const useAdditionalCharges = () =>
  useQuery({
    queryKey: keys.settings("additionalCharges"),
    queryFn: api.settings.additionalCharges,
    staleTime: 60_000,
  });

export function useSaveAdditionalCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (charge: Omit<T.AdditionalCharge, "id"> & { id?: T.Id }) =>
      api.settings.saveAdditionalCharge(charge),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("additionalCharges") }),
  });
}

export function useRemoveAdditionalCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.settings.removeAdditionalCharge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("additionalCharges") }),
  });
}

export const usePaymentOptions = () =>
  useQuery({
    queryKey: keys.settings("paymentOptions"),
    queryFn: api.settings.paymentOptions,
    staleTime: 60_000,
  });

export function useSavePaymentOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (option: T.PaymentOptionForm) => api.settings.savePaymentOption(option),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("paymentOptions") }),
  });
}

export function useRemovePaymentOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: T.Id) => api.settings.removePaymentOption(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.settings("paymentOptions") }),
  });
}

export const useCompanyProfile = () =>
  useQuery({
    queryKey: keys.settings("company"),
    queryFn: api.settings.company,
    staleTime: 60_000,
  });

export function useActOnApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: T.Id; action: "verify" | "approve" | "return"; note?: string }) =>
      api.approvals.act(input.id, input.action, input.note),
    // Approving can succeed while issuing is refused, so everything reloads either way.
    onSettled: () => {
      void settled(qc);
      void qc.invalidateQueries({ queryKey: ["documentApproval"] });
      void qc.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

export const useApprovalRules = () =>
  useQuery({ queryKey: keys.settings("approvalRules"), queryFn: api.approvals.rules });

export function useSaveApprovalRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rules, reason }: { rules: T.ApprovalRule[]; reason: string }) =>
      api.approvals.saveRules(rules, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings("approvalRules") });
      void qc.invalidateQueries({ queryKey: ["documentApproval"] });
    },
  });
}

export const useDocumentApproval = (kind: T.ApprovalDocumentKind, id: T.Id, enabled = true) =>
  useQuery({
    queryKey: ["documentApproval", kind, id] as const,
    queryFn: () => api.approvals.document(kind, id),
    enabled,
  });

export function useSubmitForApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, id }: { kind: T.ApprovalDocumentKind; id: T.Id }) =>
      api.approvals.submit(kind, id),
    onSuccess: () => {
      void settled(qc);
      void qc.invalidateQueries({ queryKey: ["documentApproval"] });
    },
  });
}

// ───────────────────────── Help and support (ADR-0010) ─────────────────────────

/**
 * Whether the shell offers Help. Long-lived: it changes with a deployment's configuration, not
 * while somebody is looking, and a failed read means no Help button rather than an error.
 */
export const useHelpStatus = () =>
  useQuery({ queryKey: keys.help, queryFn: api.help.status, staleTime: 5 * 60_000, retry: false });

export const useHelpHome = () => useQuery({ queryKey: keys.helpHome, queryFn: api.help.home });

export const useHelpCategory = (slug: string) =>
  useQuery({ queryKey: keys.helpCategory(slug), queryFn: () => api.help.category(slug) });

export const useHelpArticle = (slug: string) =>
  useQuery({ queryKey: keys.helpArticle(slug), queryFn: () => api.help.article(slug) });

/** Search as the reader types; nothing is asked for an empty box. */
export const useHelpSearch = (q: string) =>
  useQuery({
    queryKey: keys.helpSearch(q),
    queryFn: () => api.help.search(q),
    enabled: q.trim().length > 0,
    placeholderData: (prev) => prev,
  });

export const useSupportCategories = () =>
  useQuery({
    queryKey: keys.supportCategories,
    queryFn: api.support.categories,
    staleTime: 5 * 60_000,
  });

export const useSupportTickets = (q: { history: boolean; search?: string }) =>
  useQuery({
    queryKey: keys.supportTickets(q),
    queryFn: () => api.support.tickets(q),
    placeholderData: (prev) => prev,
  });

export const useSupportTicket = (id: T.Id) =>
  useQuery({ queryKey: keys.supportTicket(id), queryFn: () => api.support.ticket(id) });

/** Stages the files, then raises the ticket with their keys. */
export const useRaiseSupportTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<T.NewSupportTicket, "attachmentKeys"> & { files: File[] }) => {
      const { files, ...ticket } = input;
      const staged = files.length > 0 ? await api.support.stageFiles(files) : [];
      return api.support.raise({ ...ticket, attachmentKeys: staged.map((f) => f.stagingKey) });
    },
    onSuccess: (detail) => {
      qc.setQueryData(keys.supportTicket(detail.ticket.id), detail);
      void qc.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
  });
};

/** Stages the files, then replies with their keys. */
export const useReplyToSupportTicket = (id: T.Id) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bodyHtml, files }: { bodyHtml: string; files: File[] }) => {
      const staged = files.length > 0 ? await api.support.stageFiles(files) : [];
      return api.support.reply(
        id,
        bodyHtml,
        staged.map((f) => f.stagingKey),
      );
    },
    onSuccess: (detail) => {
      qc.setQueryData(keys.supportTicket(id), detail);
      void qc.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
  });
};
